import { readSetting, removeSetting, writeSetting } from '../../infrastructure/storage/localStore';

/**
 * Whether this DEVICE has ever had an account on it (SPEC-009 D-10).
 *
 * This flag exists to answer one question without asking Firebase: *is it worth
 * loading the auth SDK at all?* Resolving a session properly requires
 * `onAuthStateChanged`, which requires the SDK — so a provider that simply
 * checked on mount would download it for every reader, including the large
 * majority who will never sign in to a card game. That would quietly undo D-6
 * and make the shared board something signed-out readers pay for.
 *
 * So the SDK loads only when this flag is set, or when the address bar carries
 * something that looks like a sign-in link. A reader who has never signed in
 * here gets no request, no chunk and no third-party origin — which is what
 * `duelLeaderboard.spec.js` asserts, and what makes the claim in ADR-0005
 * honest.
 *
 * What it is NOT: a source of truth about being signed in. Firebase owns that,
 * the flag is only ever a hint about whether to go and ask, and it is corrected
 * the moment the SDK disagrees — a revoked or expired session clears it. Nothing
 * is gated on it, so a wrong answer costs a needless SDK load in one direction
 * and a sheet the reader has to open in the other.
 *
 * It holds no identity: not a uid, not a name, not an address. It is one boolean
 * about this browser, which is the whole of what CON-003 tolerates here.
 */

const ACCOUNT_PRESENT = 'account.present';

/** Anything other than the value we write reads as absent. */
export function hasAccountOnThisDevice() {
    return readSetting(ACCOUNT_PRESENT) === 'yes';
}

export function rememberAccountOnThisDevice() {
    return writeSetting(ACCOUNT_PRESENT, 'yes');
}

export function forgetAccountOnThisDevice() {
    return removeSetting(ACCOUNT_PRESENT);
}

/**
 * Whether this address looks like a Firebase email sign-in link.
 *
 * A cheap pre-check, and deliberately a loose one: the SDK's own
 * `isSignInWithEmailLink` is the authority and still runs. This only decides
 * whether it is worth loading the SDK to ask, on a device that has never signed
 * in — which is exactly the case of a reader opening the emailed link in a
 * browser that has no flag set yet.
 *
 * Erring towards loading is the safe direction. A false positive costs one
 * unnecessary chunk; a false negative would strand a reader on a link that
 * silently does nothing, which is the one failure in this flow with no way out.
 */
export function looksLikeEmailSignInLink(href) {
    if (typeof href !== 'string' || !href) {
        return false;
    }

    return href.includes('mode=signIn') || href.includes('oobCode=');
}
